import type { FC } from "react";
import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";

import dompurify from "isomorphic-dompurify";

import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { toast } from "@jti/core-components/components/Toaster";
import { CheckoutRoutes, standardCheckoutRoutes } from "@jti/fe-common";
import { type OrderAttributeValues } from "@jti/magento";
import { useGetProfile } from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectFirstAvailableShippingMethod } from "@jti/store/selectors/selectFirstAvailableShippingMethod";
import { selectIsZeroValueOrder } from "@jti/store/selectors/selectIsZeroValueOrder";
import { selectOrderAttributes } from "@jti/store/selectors/selectOrderAttributes";
import { selectOrderAttributesByTarget } from "@jti/store/selectors/selectOrderAttributesByTarget";
import { selectTradeInOrderAttributes } from "@jti/store/selectors/selectTradeInOrderAttributes";
import { DeliveryOptionType } from "@jti/store/slices/LEGACY__checkoutSlice";
import { loyaltySlice } from "@jti/store/slices/loyaltySlice";
import { setDeliveryStepData } from "@jti/store/slices/standardCheckoutFormSlice";
import { Form, useCountry, useForm, useTranslations } from "@jti/ui";

import { OrderAttributes } from "@checkout/components/OrderAttributes";
import { DeliveryFormShippingOptions } from "@checkout/forms/DeliveryForm/components/DeliveryFormShippingOptions";
import { useGtm } from "@checkout/hooks/gtm/useGtm";
import useDelivery from "@checkout/hooks/useDelivery";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import { LOYALTY_CHOICE, useLoyalty } from "@checkout/hooks/useLoyalty";
import { useLoyaltyStatus } from "@checkout/hooks/useLoyaltyStatus";
import { useMinimumPayableAmount } from "@checkout/hooks/useMinimumPayableAmount";
import { useSubscription } from "@checkout/hooks/useSubscription";
import { overrideOrderAttributesWithProfileExtra } from "@checkout/utils/overrideOrderAttributesWithProfileExtra";
import {
  prepareConsentOrderAttributes,
  prepareOrderAttributes,
  prepareTradeInAttributes,
} from "@checkout/utils/prepareOrderAttributes";

import { SHIPPING_FORM_FIELDS } from "../ShippingForm/constants";

import { DeliveryFormFooter } from "./components/DeliveryFormFooter";

import styles from "./DeliveryForm.module.scss";

const { sanitize } = dompurify;

export type DeliveryFormData = {
  shippingMethodCode: string;
  orderAttributes?: OrderAttributeValues;
};

type DeliveryFormProps = {
  asideSubmitButtonWrapperRef: React.RefObject<HTMLDivElement | null>;
};

export const DeliveryForm: FC<DeliveryFormProps> = ({ asideSubmitButtonWrapperRef }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { __ } = useTranslations();
  const { countryCode } = useCountry();
  const { isFreeTrial } = useFreeTrial();
  const checkoutConfiguration = useCheckoutConfiguration();
  const { hasCampaignSelected } = useLoyalty();
  const minimumPayableAmount = useMinimumPayableAmount();
  const { isSubscription } = useSubscription();
  const { dispatchGtmShippingInfo } = useGtm();
  const { data: userProfileData } = useGetProfile();

  const freeTrialCart = useSelector((state) => state.freeTrialCart);
  const isZeroValueOrder = useSelector(selectIsZeroValueOrder(isFreeTrial));
  const cart = useSelector((state) => state.cart);
  const subtotalPrice = useSelector((state) => state.cart.prices.data?.subtotal.value);
  const firstAvailableShippingMethod = useSelector((state) => selectFirstAvailableShippingMethod(state, isFreeTrial));
  const loyaltyStatus = useLoyaltyStatus();
  const { shippingStepData } = useSelector((state) => state.standardCheckoutForm);
  const otherOrderAttributes = useSelector(selectOrderAttributes);
  const tradeInOrderAttributes = useSelector(selectTradeInOrderAttributes);

  const isConsentOnPage = useSelector((state) => selectOrderAttributesByTarget(state, "buttonPlaceOrder_before"));
  const { loyaltyChoice, loyaltyCampaignId } = useSelector((state) => state.loyalty);

  const {
    cartId,
    filteredShippingMethods,
    shippingMethods,
    getDefaultShippingMethods,
    setShippingMethods,
    placeOrderAction,
    hasOrderError,
  } = useDelivery();
  const isHomeDelivery = shippingStepData?.deliveryOptionType === DeliveryOptionType.AtHome;

  const form = useForm<DeliveryFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      shippingMethodCode: shippingStepData?.shippingMethodCode,
      orderAttributes: {},
    },
    shouldUnregister: false,
  });

  const { errors, isValid } = form.formState;

  const shippingMethodCodeInput = form.watch(SHIPPING_FORM_FIELDS.shippingMethodCode);

  const method = useMemo(
    () => shippingMethods.data.find(({ methodCode }) => methodCode === shippingMethodCodeInput),
    [shippingMethods.data, shippingMethodCodeInput]
  );

  const isSubmitDisabled =
    (isHomeDelivery && !filteredShippingMethods.length) ||
    Object.keys(errors).length !== 0 ||
    !isValid ||
    !hasCampaignSelected ||
    (!isFreeTrial && minimumPayableAmount?.isReached) ||
    (!isFreeTrial && cart.items.length === 0);

  const handleMobileSubmit = () => {
    formRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  };

  const submitDeliveryForm = async (data: DeliveryFormData) => {
    if (loyaltyStatus) {
      const validationStatus = loyaltyChoice === LOYALTY_CHOICE.REDEEM && !loyaltyCampaignId;
      dispatch(loyaltySlice.actions.setLoyaltyError(validationStatus));
      if (validationStatus) return;
    }

    if (!cartId || !data.shippingMethodCode || !shippingStepData) {
      // TODO: Handle form submit error
      return;
    }

    const preparedConsentAttributes = prepareConsentOrderAttributes(data.orderAttributes);
    const preparedOrderAttributes = prepareOrderAttributes(otherOrderAttributes);
    const preparedTradeInAttributes = prepareTradeInAttributes(tradeInOrderAttributes);
    let additionalAttributes = [...preparedConsentAttributes, ...preparedOrderAttributes, ...preparedTradeInAttributes];

    const userProfileExtraAttributes = userProfileData?.extra;

    additionalAttributes = overrideOrderAttributesWithProfileExtra(additionalAttributes, userProfileExtraAttributes);

    const shippingMethod = filteredShippingMethods.find(({ methodCode }) => methodCode === data.shippingMethodCode);

    const placeOrderResult = await dispatch(
      placeOrderAction({
        cartId,
        additionalAttributes,
        isZeroValueOrder,
        shippingAddress: shippingStepData.shippingAddress,
      })
    );

    // placeOrder failed, action rejected with exception OrderExceptionCode.MagentoOrderCreationIssue and supported by the template
    if (
      !placeOrderResult.payload ||
      !("orderNumber" in placeOrderResult.payload) ||
      !("entityId" in placeOrderResult.payload)
    ) {
      return;
    }

    const { entityId, orderNumber } = placeOrderResult.payload;

    // GTM tracking for shipping info to be triggered just once before the payment step
    const trackingData = isFreeTrial ? freeTrialCart : cart;

    shippingMethod && dispatchGtmShippingInfo(trackingData, shippingMethod);

    // Early return for zero value order
    if (isZeroValueOrder) {
      history.replace(standardCheckoutRoutes.order.getSummaryUrl(orderNumber));
      return;
    }

    dispatch(
      setDeliveryStepData({
        shippingMethodCode: data.shippingMethodCode,
        orderAttributes: data.orderAttributes,
        entityId,
        orderId: orderNumber,
      })
    );

    const checkoutRoutes = new CheckoutRoutes({ isFreeTrial });
    const paymentPageUrl = checkoutRoutes.payment.getBaseUrl(orderNumber);

    history.push(paymentPageUrl);
  };

  useEffect(
    function setInitialShippingMethodCode() {
      const firstAvailableShippingMethodCode = firstAvailableShippingMethod?.methodCode;
      if (
        !isHomeDelivery ||
        !firstAvailableShippingMethodCode ||
        firstAvailableShippingMethodCode === shippingMethodCodeInput
      ) {
        return;
      }

      form.setValue(SHIPPING_FORM_FIELDS.shippingMethodCode, firstAvailableShippingMethodCode);
    },
    [firstAvailableShippingMethod]
  );

  useEffect(
    function fetchShippingMethods() {
      if (!cartId) {
        return;
      }

      dispatch(getDefaultShippingMethods({ cartId, countryCode }));
    },
    [cartId, countryCode, cart.coupons, subtotalPrice, dispatch, getDefaultShippingMethods]
  );

  // TODO: Revisit this hook as it has unnecessary invokations which cause UI glitching
  useEffect(
    function setShippingMethodsOnCart() {
      if (shippingMethods.fetchStatus !== "success") {
        return;
      }

      const isShippingMethodsPayloadReady = !!(cartId && method?.methodCode && method?.carrierCode);

      if (!isShippingMethodsPayloadReady) {
        return;
      }

      if (isSubscription && method.disableForSubscription) {
        newrelic.noticeError(
          new Error(
            "Selected shipping method (most probably, the only available) in subscription order, was disabled for subscription."
          )
        );

        return;
      }

      dispatch(
        setShippingMethods({
          cartId,
          methodCode: method.methodCode,
          carrierCode: method.carrierCode,
        })
      );
    },
    [
      cartId,
      shippingMethodCodeInput,
      cart.coupons,
      isSubscription,
      method?.methodCode,
      method?.carrierCode,
      shippingMethods.fetchStatus,
    ]
  );

  useEffect(() => {
    if (hasOrderError) {
      toast.error(__("cart.checkout.place_order.error"));
    }
  }, [hasOrderError]);

  return (
    <Form<DeliveryFormData>
      data-testid="delivery-form"
      form={form}
      onSubmit={submitDeliveryForm}
      className={styles.container}
      ref={formRef}
    >
      {isHomeDelivery && <DeliveryFormShippingOptions />}

      {isZeroValueOrder && (
        <AlertBanner
          tone="success"
          richDescription={
            <div
              data-testid="pay-on-delivery-message"
              dangerouslySetInnerHTML={{
                __html: sanitize(checkoutConfiguration.zeroValueOrder?.userNotification),
              }}
            ></div>
          }
        />
      )}

      {!isFreeTrial && minimumPayableAmount?.isReached && (
        <AlertBanner
          tone="warning"
          richDescription={
            <p data-testid="minimum-payable-amount-reached">
              {__("cart.order.message.minimum_payable_amount_reached")}
            </p>
          }
        />
      )}

      <div data-testid="consents-section">
        {isConsentOnPage?.length > 0 && (
          <div data-testid="consents-title" className={styles.consentsTitle}>
            {__("cart.checkout.consents")}
          </div>
        )}
        <OrderAttributes target={"buttonPlaceOrder_before"} />
      </div>

      <div data-testid="place-order-button">
        <DeliveryFormFooter
          isSubmitDisabled={isSubmitDisabled}
          className={styles.footerDesktop}
          data-testid="delivery-form-footer-desktop"
        />

        {/* Mobile button */}
        {asideSubmitButtonWrapperRef.current &&
          createPortal(
            <DeliveryFormFooter
              onSubmitButtonClick={handleMobileSubmit}
              isSubmitDisabled={isSubmitDisabled}
              className={styles.footerMobile}
              data-testid="delivery-form-footer-mobile"
            />,
            asideSubmitButtonWrapperRef.current
          )}
      </div>
    </Form>
  );
};
